import { Button } from "@admin/components/atoms/Button/Button";
import { MultiSelectDropdown } from "@admin/components/atoms/Input/MultiSelect";
import { Modal, ModalProps } from "@admin/components/atoms/Modal/Modal";
import {
  copyShifts,
  CopyShiftsDayPayload,
  dateToISO,
  LocationUser,
} from "@enos-monorepo/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getISOWeek } from "date-fns";
import { useCallback,useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { WeekSelection } from "./WeekSelection";

interface PersonelOptions {
  label: string;
  value: string;
}

export interface WeekModel {
  label: string;
  dateRange: DateRange;
}

interface CopyShiftWeek extends ModalProps {
  open: boolean;
  personnelList: LocationUser[];
  selectedWeek: DateRange;
  shiftsLength: number;
  companyId: string;
}

export function CopyShiftsWeekModal({
  open,
  personnelList,
  selectedWeek,
  shiftsLength,
  companyId,
  ...props
}: CopyShiftWeek) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({ mutationFn: copyShifts });
  const [isCopyOpen, setIsCopyOpen] = useState(true);
  const [selectedPersonnel, setSelectedPersonnel] = useState<LocationUser[]>(
    [],
  );
  const [selectedWeekRange, setSelectedWeekRange] = useState<
    DateRange | undefined
  >(undefined);
  const [selectedWeeks, setSelectedWeeks] = useState<WeekModel[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const personnelOptions: PersonelOptions[] = personnelList?.map(
    (personnel) => ({
      label: personnel.firstName + " " + personnel.lastName,
      value: personnel.userId,
    }),
  );

  const getWeekNumber = useCallback((date: Date) => {
    const copyDate = new Date(date.getTime());
    copyDate.setUTCDate(
      copyDate.getUTCDate() + 4 - (copyDate.getUTCDay() || 7),
    );
    const yearStart = new Date(Date.UTC(copyDate.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil(
      ((copyDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
    );

    if (weekNumber < 1) {
      return getWeekNumber(new Date(copyDate.getUTCFullYear() - 1, 11, 31));
    }
    return weekNumber + 1;
  }, []);

  useEffect(() => {
    if (!selectedWeekRange?.from) return;

    const weekNumber = getWeekNumber(selectedWeekRange.from);
    const weekLabel = `${t("common.Week")} ${weekNumber}`;

    const isWeekSelected = selectedWeeks.some((week) =>
      isSameWeek(week, weekLabel, selectedWeekRange),
    );

    if (!isWeekSelected) {
      setSelectedWeeks((prevWeeks) => [
        ...prevWeeks,
        { label: weekLabel, dateRange: selectedWeekRange },
      ]);
    } else {
      toast.error(t("shift.This week is already selected"));
    }

    setSelectedWeekRange(undefined);
  }, [getWeekNumber, selectedWeekRange, selectedWeeks, t]);

  const isSameWeek = (
    week: WeekModel,
    weekLabel: string,
    selectedWeekRange: DateRange,
  ) => {
    return (
      week.label === weekLabel &&
      week.dateRange.from?.getTime() === selectedWeekRange.from?.getTime() &&
      week.dateRange.to?.getTime() === selectedWeekRange.to?.getTime()
    );
  };

  const handleCopyShiftsDay = () => {
    if (selectedPersonnel.length === 0) {
      toast.error(t("shift.Please select personnel"));
      return;
    }

    if (selectedWeeks.length === 0) {
      toast.error(t("shift.Please select target weeks"));
      return;
    }

    if (shiftsLength === 0) {
      toast.error(t("shift.No shifts found to copy"));
      return;
    }

    const users = selectedPersonnel.map((personnel) => personnel.userId);

    const destinationShiftDates = selectedWeeks.map((week) => ({
      assignStartDate: week.dateRange?.from
        ? dateToISO(week.dateRange?.from)
        : "",
      assignEndDate: week.dateRange?.to ? dateToISO(week.dateRange?.to) : "",
    }));

    const payload = {
      copyStartDate: selectedWeek?.from ? dateToISO(selectedWeek?.from) : "",
      copyEndDate: selectedWeek?.to ? dateToISO(selectedWeek?.to) : "",
      destinationShiftDates: destinationShiftDates,
      companyLocationId: personnelList[0]?.companyLocationId || "",
      companyId: companyId,
      users: users,
      includeOpenShifts: isCopyOpen,
    } as CopyShiftsDayPayload;

    mutate(payload, {
      onSuccess: () => {
        toast.success(t("shift.The shifts have been copied"));
        queryClient.invalidateQueries({ queryKey: ["shifts"] });
        props.onClose?.();
        clearModal();
      },
      onError: () => {
        toast.error(t("shift.Unable to copy the shifts"));
      },
    });
  };

  const handleDiscard = () => {
    props.onClose?.();
    clearModal();
  };

  const clearModal = () => {
    setSelectedPersonnel([]);
    setSelectedWeekRange(undefined);
    setSelectedWeeks([]);
    setIsCopyOpen(true);
    setIsOpen(false);
  };

  return (
    <Modal
      open={open}
      title={t("shift.Copy Shifts of Week to", {
        week: getISOWeek(selectedWeek?.from ?? ""),
      })}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleDiscard}
            disabled={isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            onClick={handleCopyShiftsDay}
            disabled={isPending}
          >
            {t("shift.Copy Shifts")}
          </Button>
        </>
      }
      {...props}
    >
      <div className="flex flex-col w-100 gap-5">
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("common.Select Personnel")}
          </label>
          <div className="flex flex-col w-full">
            <MultiSelectDropdown
              variant="filled"
              placeholder={t("common.Select Personnel")}
              options={personnelOptions}
              selectedOptions={selectedPersonnel.map((personnel) => ({
                label: personnel.firstName + " " + personnel.lastName,
                value: personnel.userId.toString(),
              }))}
              onSelect={(selected) =>
                setSelectedPersonnel(
                  personnelList.filter((personnel) =>
                    selected.some(
                      (option) => option.value === personnel.userId.toString(),
                    ),
                  ),
                )
              }
            />
          </div>
        </div>

        <WeekSelection
          selectedWeek={selectedWeek}
          selectedWeeks={selectedWeeks}
          setSelectedWeeks={setSelectedWeeks}
          setSelectedWeekRange={setSelectedWeekRange}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />

        {/*
        <div className="flex items-baseline flex-row lg:flex-nowrap gap-2.5">
          <label className="form-label flex items-center gap-1 max-w-56 !font-normal !text-gray-800">
            {t("shift.Copy Open Shifts")}?
          </label>
          <div className="flex flex-col w-full">
            <Select
              options={[
                { label: t("common.Yes"), value: "Yes" },
                { label: t("common.No"), value: "No" },
              ]}
              placeholder={t("common.Select an option")}
              defaultValue={isCopyOpen ? t("common.Yes") : t("common.No")}
              onChange={(e) => setIsCopyOpen(e.target.value === "Yes")}
            />
          </div>
        </div>
        */}
      </div>
    </Modal>
  );
}
